import React, { memo, useCallback, useMemo } from 'react';
import noop from 'lodash/noop';
import { SpotlightTarget } from '@atlaskit/onboarding';
import {
	ActionsWrapper,
	LeftActionsWrapper,
	RightActionsWrapper,
} from '@atlassian/jira-business-app-wrapper';
import { isOnboardingForLinkedIssuesEnabledInJwmBoard } from '@atlassian/jira-business-feature-flags';
import { type QuickFilterKeys, SimpleSearchField, Filters } from '@atlassian/jira-business-filters';
import {
	integrationTypes,
	JWMShareButton,
	AddPeopleWithAvatar,
} from '@atlassian/jira-business-invite-people';
import { MoreMenuCore } from '@atlassian/jira-business-more-menu';
import {
	BOARD_EMBED_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-board-embed';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ASSIGNEE_TYPE,
	ISSUE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { getSupportedExtendedFilterTypes } from '../../common/constants';
import { useJWMBoardFeatures } from '../../controllers/features-context';
import { useWorkflowStoreState } from '../../controllers/workflow-store';
import { useBoardStore, useHasMultipleWorkflows } from '../../services/board-store';
import type { Issue } from '../../services/board-store/types';
import GroupSelector from './group-selector';
import { IssueLinksNudgeSpotlight } from './issue-links-nudge-spotlight';
import messages from './messages';
import { MoreMenuContent } from './more-menu-content';

const MORE_MENU_MIN_WIDTH = 260;
const WORKFLOWS_MENU_WIDTH = 320;
const HIDDEN_QUICKFILTERS: QuickFilterKeys[] = ['DONE', 'ACTIVE_SPRINTS'];

const Header = () => {
	const { isShareButtonEnabled, inviteButtonInteraction, isPresenceEnabled } =
		useJWMBoardFeatures();
	const [{ nudgeStep, shouldOpenModal }, { setShouldOpenModal }] = useNudgeStep();

	let issues: Issue[] | undefined;
	let hasMultipleWorkflows: boolean;
	if (ff('jwm.board-awesome-enhancements')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { workflows } = useWorkflowStoreState();
		hasMultipleWorkflows = workflows.length > 1;
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		[{ issues }] = useBoardStore();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		hasMultipleWorkflows = useHasMultipleWorkflows();
	}

	const allowedFieldTypes = useMemo(
		() =>
			new Set<string>([
				ASSIGNEE_TYPE,
				ISSUE_TYPE,
				PRIORITY_TYPE,
				...getSupportedExtendedFilterTypes(),
				STATUS_TYPE,
			]),
		[],
	);

	const moreMenuContent = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'closeMenu' implicitly has an 'any' type.
		(closeMenu) => <MoreMenuContent useCloseMenu={closeMenu ?? noop} />,
		[],
	);

	const handleDialogClose = useCallback(() => {
		if (shouldOpenModal) {
			setShouldOpenModal(false);
		}
	}, [setShouldOpenModal, shouldOpenModal]);

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.BOARD}
			shareContentType="board"
			isAutoOpenDialog={shouldOpenModal}
			onDialogClose={handleDialogClose}
		/>
	);

	const showSpotlight = useMemo(() => nudgeStep === BOARD_EMBED_NUDGE_STEP, [nudgeStep]);

	const wrappedShareButton = showSpotlight ? (
		<SpotlightTarget name={BOARD_EMBED_NUDGE_STEP}>
			<div>{shareButton}</div>
		</SpotlightTarget>
	) : (
		shareButton
	);

	const searchBar = <SimpleSearchField placeholder={messages.searchBoardLabel} view="board" />;
	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="board"
			allowedFieldTypes={allowedFieldTypes}
			isPresenceEnabledInContext={isPresenceEnabled}
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
		/>
	);

	const shareActionButton = isShareButtonEnabled && wrappedShareButton;

	const hasIssues = issues != null && issues.length > 0;

	const moreMenu = isOnboardingForLinkedIssuesEnabledInJwmBoard() ? (
		<IssueLinksNudgeSpotlight>
			<MoreMenuCore
				renderContent={moreMenuContent}
				minWidth={hasMultipleWorkflows ? WORKFLOWS_MENU_WIDTH : MORE_MENU_MIN_WIDTH}
			/>
		</IssueLinksNudgeSpotlight>
	) : (
		<MoreMenuCore
			renderContent={moreMenuContent}
			minWidth={hasMultipleWorkflows ? WORKFLOWS_MENU_WIDTH : MORE_MENU_MIN_WIDTH}
		/>
	);

	return (
		<UFOSegment name="business-board-header">
			<ActionsWrapper>
				<LeftActionsWrapper>
					{searchBar}
					{addPeopleWithPresence}
				</LeftActionsWrapper>

				<RightActionsWrapper>
					{shareActionButton}
					<Filters
						view="board"
						allowedFieldTypes={allowedFieldTypes}
						hiddenQuickFilters={HIDDEN_QUICKFILTERS}
						allowCustomFields
					/>
					{(ff('jwm.board-awesome-enhancements') || hasIssues) && <GroupSelector />}
					{moreMenu}
				</RightActionsWrapper>
			</ActionsWrapper>
		</UFOSegment>
	);
};

export default memo(Header);
